import { useRef, useState } from 'react'
import { Paperclip, Loader2, Trash2, FileText, Image as ImageIcon, Upload } from 'lucide-react'
import ConfirmDialog from './ConfirmDialog.jsx'
import { uploadFile } from '../lib/uploadDirect.js'

/**
 * Attach images / PDFs to a record that does not exist yet.
 *
 * Different from `purchase/PurchaseFiles.jsx` in one way that matters: there, the
 * PO already exists, so every action saves immediately. Here the leave request or
 * the warning is still a draft, so the file goes to Wasabi at once (it has to —
 * we cannot hold a 20 MB PDF in React state) and the returned `entry` is held in
 * the parent's draft until บันทึก.
 *
 * The cost of that is an orphaned object if the user abandons the form: a few
 * hundred KB in a folder that is already theirs. The alternative — create the
 * record first, then attach — leaves half-filed requests in HR's queue, which is
 * a far worse thing to look at every morning.
 *
 * `uploadUrl` differs by caller, and so does the folder the object lands in:
 *   · /api/me/leave-file      → the CALLER's own folder
 *   · /api/hr/warning-upload  → the TARGET employee's folder (`extra.employee_id`)
 * Both write into bucket `userssdc`, so one object is readable through
 * /api/me/file/{key} by the employee and /api/hr/file/{key} by HR.
 *
 * 🔑 The bytes go through `uploadFile`, which presigns and sends them browser →
 * Wasabi. This component used to POST raw FormData at `uploadUrl`, so a 20 MB
 * scan of a signed หนังสือเตือน was buffered three times over inside the 128 MB
 * isolate that every /api route shares — one of the paths behind the Error 1102
 * outages (src/lib/uploadDirect.js has the incident). Both routes still accept
 * multipart, and uploadFile falls back to it by itself if the bucket's CORS is
 * not set yet, so nothing here breaks while that is outstanding.
 */
export default function AttachPicker({
  files = [],
  onChange,
  uploadUrl,
  extra = {},
  disabled = false,
  hint = 'แนบรูปภาพหรือไฟล์ PDF',
  max = 5,
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirmFile, setConfirmFile] = useState(null)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef(null)
  // dragenter/dragleave fire for every child the pointer crosses, so a plain
  // boolean flickers. Count enters minus leaves instead.
  const dragDepth = useRef(0)

  const list = Array.isArray(files) ? files : []
  const full = list.length >= max

  async function upload(picked) {
    const chosen = [...(picked || [])]
    if (!chosen.length || disabled) return
    setBusy(true)
    setError('')
    try {
      let next = list
      for (const f of chosen) {
        if (next.length >= max) break
        let out
        try {
          out = await uploadFile(uploadUrl, extra, f)
        } catch (e) {
          throw new Error(uploadError(e, f))
        }
        // Both routes answer `{ ok, entry }` on either road. Anything else means
        // the record would hold an `undefined` in its file[] — refuse instead.
        if (!out?.entry?.location) throw new Error(`“${f.name}” อัปโหลดไม่สำเร็จ — ไม่ได้รับข้อมูลไฟล์กลับมา`)
        next = [...next, out.entry]
      }
      onChange?.(next)
    } catch (e) {
      setError(e.message || 'อัปโหลดไม่สำเร็จ')
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  function remove(entry) {
    setConfirmFile(null)
    onChange?.(list.filter((f) => f.uuid !== entry.uuid))
  }

  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files')
  const canDrop = !disabled && !busy && !full

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[12.5px] font-medium text-slate-500">
          ไฟล์แนบ {list.length > 0 && <span className="tabular-nums">({list.length}/{max})</span>}
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || busy || full}
          className="text-[12.5px] text-brand-600 hover:text-brand-700 disabled:opacity-40 flex items-center gap-1"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />} แนบไฟล์
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => upload(e.target.files)}
        />
      </div>

      {list.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 mb-2">
          {list.map((f) => {
            const isImg = String(f.type || '').startsWith('image/')
            const Icon = isImg ? ImageIcon : FileText
            return (
              <div key={f.uuid || f.location} className="flex items-center gap-2.5 px-3 py-2">
                <span className="shrink-0 w-8 h-8 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center">
                  <Icon size={14} className="text-slate-400" />
                </span>
                <span className="flex-1 min-w-0 text-[12.5px] text-slate-700 truncate">{f.name || f.location}</span>
                <span className="text-[11px] text-slate-400 tabular-nums shrink-0">{fmtSize(f.size)}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => setConfirmFile(f)}
                    className="p-1 text-slate-300 hover:text-rose-600"
                    title="เอาไฟล์นี้ออก"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {!full && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || busy}
          onDragEnter={(e) => { if (canDrop && hasFiles(e)) { e.preventDefault(); dragDepth.current += 1; setDragging(true) } }}
          onDragOver={(e) => { if (canDrop && hasFiles(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' } }}
          onDragLeave={(e) => { if (!canDrop) return; e.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragging(false) }}
          onDrop={(e) => { if (!canDrop || !hasFiles(e)) return; e.preventDefault(); dragDepth.current = 0; setDragging(false); upload(e.dataTransfer.files) }}
          className={`w-full rounded-xl border-2 border-dashed px-4 py-4 text-center text-[12px] transition disabled:pointer-events-none ${
            dragging
              ? 'border-brand-400 bg-brand-50/60 text-brand-700'
              : 'border-slate-200 bg-white/50 text-slate-400 hover:border-brand-300 hover:text-brand-600 hover:bg-brand-50/40'
          }`}
        >
          <Upload size={16} className="mx-auto mb-1 text-slate-300" />
          {hint}
        </button>
      )}

      {error && <p className="mt-1.5 text-[12px] text-rose-600">{error}</p>}

      {confirmFile && (
        <ConfirmDialog
          title="เอาไฟล์นี้ออก?"
          message={`“${confirmFile.name}” จะถูกเอาออกจากรายการนี้`}
          confirmLabel="เอาออก"
          tone="danger"
          onConfirm={() => remove(confirmFile)}
          onCancel={() => setConfirmFile(null)}
        />
      )}
    </div>
  )
}

function fmtSize(bytes) {
  const n = Number(bytes) || 0
  if (!n) return ''
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

/**
 * The upload routes answer a refusal with JSON that already names the cause
 * ({error, status, detail, hint}). Swallowing that into "(502)" is what once turned
 * a bucket-permission problem into a mystery — and `userssdc` is exactly the bucket
 * whose policy has been the open item since /me/setting shipped, so an AccessDenied
 * here is a real and likely answer that deserves to be readable.
 *
 * It now reads the Error `uploadFile` throws rather than a Response, because the
 * bytes no longer go through a fetch this component makes. That parsed body rides
 * along on `e.body` for exactly this purpose — see `refusal()` in
 * src/lib/uploadDirect.js — so every branch below is the one it always was.
 */
function uploadError(e, file) {
  const payload = e?.body || null
  const status = e?.status
  const name = file?.name ? `“${file.name}” ` : ''
  const raw = String(payload?.detail || payload?.error || '')
  const s3 = raw.match(/<Code>([^<]+)<\/Code>/)?.[1] || ''

  if (payload?.error === 'file_too_large') return payload.detail
  if (payload?.error === 'unsupported_type') return payload.detail
  if (payload?.error === 'wasabi_not_configured') return 'ยังไม่ได้ตั้งค่าที่เก็บไฟล์ (Wasabi) บนเซิร์ฟเวอร์'
  if (status === 403 && payload?.error === 'forbidden') return payload.detail || 'ไม่มีสิทธิ์แนบไฟล์'
  if (s3 === 'AccessDenied' || payload?.status === 403) {
    return `${name}อัปโหลดไม่สำเร็จ — ที่เก็บไฟล์ปฏิเสธสิทธิ์ (AccessDenied) กรุณาแจ้งผู้ดูแลระบบ`
  }
  if (payload?.detail) return `${name}${payload.detail}`
  if (payload?.error) return `${name}อัปโหลดไม่สำเร็จ — ${payload.error}`
  // No parsed body: a network failure, or a fallback that never reached the route.
  // uploadFile's message is already a Thai sentence in that case.
  return status ? `${name}อัปโหลดไม่สำเร็จ (${status})` : `${name}${e?.message || 'อัปโหลดไม่สำเร็จ'}`
}
